import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconDeleteTable, iconEditTable } from '../../assets';
import { useDebounce } from '../../hooks/useDebounce';
import { useTheme } from '../../theme';
import { UsersFilter } from './UsersFilter';
import type { FetchUsersResponse, User, UserProps } from './types';

const DEBOUNCE_MS = 400;
const DEFAULT_PAGE_SIZE = 10;
const ACTION_ICON_SIZE = 36;

export function Users({
  fetchUsers,
  title,
  subtitle,
  searchPlaceholder,
  addUserLabel,
  onAddUser,
  onEdit,
  onDelete,
  searchDebounceMs = DEBOUNCE_MS,
}: UserProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const titleText = title ?? t('user_management.title');
  const subtitleText = subtitle ?? t('user_management.subtitle');
  const searchPlaceholderText =
    searchPlaceholder ?? t('user_management.search_placeholder');
  const addUserLabelText = addUserLabel ?? t('user_management.add_user');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, searchDebounceMs);
  const [data, setData] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res: FetchUsersResponse = await fetchUsers({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setData(res.data);
      setTotal(res.total);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('user_management.column_user'),
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.md,
                minWidth: 0,
              }}
            >
              {/* <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: theme.shape.borderRadius.sm,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: theme.typography.fontWeight.semibold,
                  fontSize: theme.typography.fontSize.sm,
                  flexShrink: 0,
                }}
              >
                {getInitials(u.name)}
              </div> */}
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.palette.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.name ?? u.email ?? '—'}
                </div>
                <div
                  style={{
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.email}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'roles',
        header: t('user_management.column_roles'),
        cell: ({ row }) => {
          const roles = row.original.roles ?? [];
          const roleLabels = roles.map(r => (typeof r === 'string' ? r : r.name));
          return (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.spacing.xs,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {roleLabels.map((label, i) => (
                <span
                  key={(typeof roles[i] === 'object' && roles[i] != null && 'id' in roles[i] ? (roles[i] as { id: string }).id : undefined) ?? i}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    borderRadius: theme.shape.borderRadius.sm2,
                    backgroundColor: theme.palette.roles.background,
                    borderWidth: theme.shape.borderWidth.thin,
                    borderStyle: 'solid',
                    borderColor: theme.palette.roles.border,
                    color: theme.palette.roles.text,
                    fontSize: theme.typography.fontSize.xs,
                    fontWeight: theme.typography.fontWeight.medium,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'group',
        header: t('user_management.column_group'),
        cell: ({ row }) => {
          const groups = row.original.userGroups ?? [];
          const group =
            row.original.group ??
            (groups.length
              ? groups
                  .map(g => (typeof g === 'string' ? g : g?.name ?? ''))
                  .filter(Boolean)
                  .join(', ')
              : null);
          return (
            <span style={{ color: theme.palette.text.primary }}>
              {group ?? t('user_management.empty_cell')}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: t('user_management.column_status'),
        cell: ({ row }) => {
          const isInactive =
            row.original.isActive === false ||
            (row.original.status &&
              String(row.original.status).toLowerCase() === 'inactive');
          const status = isInactive
            ? t('user_management.status_inactive')
            : (row.original.status ?? t('user_management.status_active'));
          const statusStyle = isInactive
            ? {
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                borderRadius: theme.shape.borderRadius.sm2,
                backgroundColor: 'rgba(185, 28, 28, 0.1)',
                borderWidth: theme.shape.borderWidth.thin,
                borderStyle: 'solid',
                borderColor: theme.palette.error.main,
                color: theme.palette.error.main,
                fontSize: theme.typography.fontSize.xs,
                fontWeight: theme.typography.fontWeight.medium,
              }
            : {
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                borderRadius: theme.shape.borderRadius.sm2,
                backgroundColor: theme.palette.status.background,
                borderWidth: theme.shape.borderWidth.thin,
                borderStyle: 'solid',
                borderColor: theme.palette.status.border,
                color: theme.palette.status.text,
                fontSize: theme.typography.fontSize.xs,
                fontWeight: theme.typography.fontWeight.medium,
              };
          return <span style={statusStyle}>{status}</span>;
        },
      },
      {
        accessorKey: 'joinDate',
        header: t('user_management.column_join_date'),
        cell: ({ row }) => (
          <span
            style={{
              color: theme.palette.text.secondary,
              whiteSpace: 'nowrap',
            }}
          >
            {row.original.joinDate ?? t('user_management.empty_cell')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('user_management.column_actions'),
        cell: ({ row }) => {
          const u = row.original;
          const isInactive =
            u.isActive === false ||
            (u.status && String(u.status).toLowerCase() === 'inactive');
          return (
            <div
              style={{
                display: 'flex',
                gap: 0,
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {onEdit && (
                <button
                  type="button"
                  className="user-mgmt-btn-icon"
                  onClick={() => onEdit(u)}
                  aria-label={t('user_management.edit_aria')}
                  data-e2e="users-edit-button"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    padding: 0,
                  }}
                >
                  <img
                    src={iconEditTable}
                    alt="Edit"
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
              {onDelete && !isInactive && (
                <button
                  type="button"
                  className="user-mgmt-btn-icon"
                  onClick={() => onDelete(u)}
                  aria-label={t('user_management.delete_aria')}
                  data-e2e="users-delete-button"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.error.main,
                    padding: 0,
                  }}
                >
                  <img
                    src={iconDeleteTable}
                    alt="Delete"
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [theme, onEdit, onDelete, t]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount,
    state: { pagination },
    onPaginationChange: updater => {
      setPagination(prev =>
        typeof updater === 'function' ? updater(prev) : prev
      );
    },
  });

  const containerStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    fontFamily: theme.typography.fontFamily,
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xs,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize['2xl'],
    color: theme.palette.text.primary,
  };

  const subtitleStyle: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.secondary,
  };

  const tableWrapperStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
  };

  const ACTION_COLUMN_WIDTH = 120;
  const STATUS_COLUMN_WIDTH = 100;
  const JOIN_DATE_COLUMN_WIDTH = 150;

  const getThWidth = (columnId: string): React.CSSProperties => {
    if (columnId === 'actions')
      return {
        width: ACTION_COLUMN_WIDTH,
        minWidth: ACTION_COLUMN_WIDTH,
        maxWidth: ACTION_COLUMN_WIDTH,
      };
    if (columnId === 'status')
      return { width: STATUS_COLUMN_WIDTH, minWidth: STATUS_COLUMN_WIDTH };
    if (columnId === 'joinDate')
      return {
        width: JOIN_DATE_COLUMN_WIDTH,
        minWidth: JOIN_DATE_COLUMN_WIDTH,
      };
    return {};
  };

  const getTdWidth = (columnId: string): React.CSSProperties =>
    getThWidth(columnId);

  const getThAlign = (columnId: string): React.CSSProperties => {
    if (columnId === 'actions') return { textAlign: 'center' };
    return {};
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.base,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    verticalAlign: 'middle',
  };

  const paginationStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  };

  const buttonClickStyle = `
    .user-mgmt-btn { transition: transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease, filter 0.12s ease; }
    .user-mgmt-btn:active { transform: scale(0.97); }
    .user-mgmt-btn-icon { transition: transform 0.12s ease, opacity 0.12s ease; }
    .user-mgmt-btn-icon:active { transform: scale(0.9); opacity: 0.8; }
  `;

  return (
    <div style={containerStyle} data-e2e="users-list">
      <style>{buttonClickStyle}</style>
      <h1 style={headerStyle}>{titleText}</h1>
      <p style={subtitleStyle}>{subtitleText}</p>

      <UsersFilter
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={searchPlaceholderText}
        searchAriaLabel={t('user_management.search_aria')}
        onAddUser={onAddUser}
        addUserLabel={addUserLabelText}
        addUserAriaLabel={t('user_management.add_user')}
        dataE2eSearchInput="users-search-input"
        dataE2eAddButton="users-create-button"
      />

      <div style={tableWrapperStyle}>
        {loading ? (
          <div
            style={{
              padding: theme.spacing['2xl'],
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            {t('user_management.loading')}
          </div>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          ...thStyle,
                          ...getThWidth(header.column.id),
                          ...getThAlign(header.column.id),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  const userId =
                    (row.original as Partial<User>)?.id ?? undefined;
                  return (
                    <tr
                      key={row.id}
                      data-e2e={userId ? `users-row-${userId}` : 'users-row'}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            ...tdStyle,
                            ...getTdWidth(cell.column.id),
                            ...(cell.column.id === 'actions'
                              ? { textAlign: 'center' as const }
                              : {}),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={paginationStyle}>
              <span
                style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.palette.text.secondary,
                }}
              >
                {t('user_management.pagination.summary', {
                  count: data.length,
                  total,
                })}
              </span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                }}
              >
                <button
                  type="button"
                  className="user-mgmt-btn"
                  disabled={!table.getCanPreviousPage()}
                  onClick={() => table.previousPage()}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.secondary,
                    cursor: table.getCanPreviousPage()
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: table.getCanPreviousPage() ? 1 : 0.6,
                  }}
                >
                  {t('user_management.previous')}
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map(
                  pageNum => {
                    const isActive = pagination.pageIndex + 1 === pageNum;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className="user-mgmt-btn"
                        onClick={() => table.setPageIndex(pageNum - 1)}
                        style={{
                          minWidth: 32,
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          fontSize: theme.typography.fontSize.sm,
                          fontFamily: theme.typography.fontFamily,
                          border: isActive
                            ? 'none'
                            : `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          backgroundColor: isActive
                            ? theme.palette.primary.main
                            : theme.palette.background.paper,
                          color: isActive
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                          cursor: 'pointer',
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
                <button
                  type="button"
                  className="user-mgmt-btn"
                  disabled={!table.getCanNextPage()}
                  onClick={() => table.nextPage()}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    cursor: table.getCanNextPage()
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: table.getCanNextPage() ? 1 : 0.6,
                  }}
                >
                  {t('user_management.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
